import React, {useCallback, useRef, useState} from 'react';
import {useClickOutside} from 'app/utils/useClickOutside';
import {getImageDomain, getLinkPath, scrollToTop} from 'app/utils';
import I18n from 'app/i18n';
import {Link} from 'react-router-dom';
import MultiLangsInterface from 'app/interfaces/MultiLangsInterface';
import PromoNewItemInterface from 'app/interfaces/articles/PromoNewItemInterface';
import {configStore} from 'app/store/global';
import {pushAnalyticsEvent} from 'app/utils/analytics';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import PromoNewCardTimer from './PromoNewCardTimer';
import PromoNewCardPrize from './PromoNewCardPrize';
import PromoNewCardTurnover from './PromoNewCardTurnover';
import PromoNewCardFooterActions from './PromoNewCardFooterActions';

interface PromoNewCardProps {
    data: PromoNewItemInterface;
    hideExpiredPromoNewIndexItem: (id: number) => void;
}

const getLabelByLocale = (langs?: MultiLangsInterface): string => {
    if (!langs) {
        return '';
    }

    return langs[I18n.locale] || langs[configStore.DEFAULT_LOCALE] || '';
};

const PromoNewCard = ({data, hideExpiredPromoNewIndexItem}: PromoNewCardProps): React.ReactElement => {
    const {id, preview_image, slug, promo_type, started_at, ended_at, promo_bonus, participants_count,
        title_langs, short_description_langs, promo_label_langs, promo_text_langs} = data;
    const cardTitle = getLabelByLocale(title_langs);
    const promoLabel = getLabelByLocale(promo_label_langs);
    const promoText = getLabelByLocale(promo_text_langs);
    const shortDescription = getLabelByLocale(short_description_langs);
    const [isInfoOpen, setIsInfoOpen] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);
    const isPermanent = promo_type === 'permanent';
    const canParticipate = Boolean(promo_bonus && !promo_bonus.auto_enrollment);
    const isParticipating = promo_bonus?.participation?.participating === true;

    const handlePromotionClick = useCallback(() => {
        pushAnalyticsEvent('promotion_click', {
            promotion_id: id,
            promotion_type: promo_type,
            source: 'promotion_list'
        });
        scrollToTop();
    }, [id, promo_type]);

    const handleInfoToggle = useCallback(() => {
        setIsInfoOpen(prevOpen => !prevOpen);
    }, []);

    const handleExpire = useCallback(() => {
        hideExpiredPromoNewIndexItem(id);
    }, [hideExpiredPromoNewIndexItem, id]);

    useClickOutside(cardRef, handleInfoToggle, isInfoOpen);

    return (
        <div
            ref={cardRef}
            className={`promo-new-card${isInfoOpen ? ' promo-new-card--info-open' : ''}${isParticipating ? ' promo-new-card--participating' : ''}`}
        >
            <div className="promo-new-card__image-wrap">
                {preview_image && <img
                    src={getImageDomain(false) + preview_image}
                    className="promo-new-card__image"
                    alt={cardTitle}
                />}
                <div className="promo-new-card__overlay">
                    <div className="promo-new-card__overlay-inner">
                        <span className="promo-new-card__badge">
                            {I18n.t(`promo_new_header_${promo_type}`)}
                        </span>
                        <PromoNewCardTimer
                            promoType={promo_type}
                            repeatability={promo_bonus?.repeatability}
                            startTime={started_at}
                            endTime={ended_at}
                            onExpire={handleExpire}
                        />
                    </div>
                </div>
            </div>
            <div className="promo-new-card__body">
                <h3 className="promo-new-card__title">{cardTitle}</h3>
                <PromoNewCardPrize
                    isPermanent={isPermanent}
                    isParticipating={isParticipating}
                    promoLabel={promoLabel}
                    promoText={promoText}
                    reward={promo_bonus?.reward}
                />
                <PromoNewCardTurnover
                    isPermanent={isPermanent}
                    isParticipating={isParticipating}
                    progress={promo_bonus?.participation?.progress}
                />
                <div className="promo-new-card__footer">
                    <div className="promo-new-card__participate">
                        <PromoNewCardFooterActions
                            isPermanent={isPermanent}
                            slug={slug}
                            canParticipate={canParticipate}
                            isParticipating={isParticipating}
                            participantsCount={participants_count ?? 0}
                            promoId={id}
                            cardTitle={cardTitle}
                            awardMode={promo_bonus?.award_mode}
                            onPromotionClick={handlePromotionClick}
                        />
                    </div>
                    <button
                        type="button"
                        className="promo-new-card__info"
                        onClick={handleInfoToggle}
                    >
                        <SpriteIcon name="#svg-info-transparent" size={{height: 16, width: 16}} />
                    </button>
                </div>
            </div>
            <div className={`promo-new-card__info-panel${isInfoOpen ? ' promo-new-card__info-panel--visible' : ''}`}
            >
                <h4 className="promo-new-card__info-panel-title">{cardTitle}</h4>
                <div className="promo-new-card__info-panel-content" dangerouslySetInnerHTML={{__html: shortDescription}} />
                <Link
                    to={getLinkPath(`/promotions/${slug}`)}
                    className="promo-new-card__info-panel-link"
                    onClick={handlePromotionClick}
                >
                    {I18n.t('full_description')}
                </Link>
                <button
                    type="button"
                    className="promo-new-card__info-panel-close"
                    onClick={handleInfoToggle}
                >
                    <SpriteIcon name="#svg-close" size={{height: 10, width: 10}} />
                </button>
            </div>
        </div>
    );
};

export default PromoNewCard;
